import { useTranslations } from "next-intl";
import { Mic, MicOff, Video, VideoOff, ChevronDown, Check } from "lucide-react";

type DeviceKind = "audio" | "video";

interface DeviceSelectorProps {
  kind: DeviceKind;
  devices: MediaDeviceInfo[];
  selectedDeviceId: string;
  isEnabled: boolean;
  isMenuOpen: boolean;
  label: string;
  onSelect: (deviceId: string) => void;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  zIndex?: number;
}

/**
 * Dropdown selector for audio/video devices.
 */
export const DeviceSelector = ({
  kind,
  devices,
  selectedDeviceId,
  isEnabled,
  isMenuOpen,
  label,
  onSelect,
  onToggleMenu,
  onCloseMenu,
  zIndex = 10
}: DeviceSelectorProps) => {
  const t = useTranslations();
  const Icon = kind === "audio" ? (isEnabled ? Mic : MicOff) : isEnabled ? Video : VideoOff;

  return (
    <div className={`relative z-${zIndex}`}>
      <button
        type="button"
        onClick={onToggleMenu}
        className={`flex items-center gap-1.5 sm:gap-2 px-2.5 py-2 sm:px-4 sm:py-2 bg-white border rounded-full shadow-sm hover:border-gray-300 hover:bg-gray-50 transition-all ${
          isMenuOpen ? "border-primary ring-2 ring-primary/10" : "border-gray-200"
        }`}
      >
        <div className={`${isEnabled ? "text-gray-700" : "text-error"}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="sm:hidden text-xs font-medium text-gray-700">{t("common.default")}</span>
        <span className="hidden sm:inline truncate max-w-[150px] text-sm font-medium text-gray-700">{label}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 transition-transform ${isMenuOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isMenuOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={onCloseMenu} />
          <div className="fixed left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-xs sm:absolute sm:left-0 sm:top-full sm:translate-x-0 sm:translate-y-0 sm:w-auto min-w-[200px] mt-0 sm:mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-40 p-1 max-h-64 overflow-y-auto">
            {devices.map((device, index) => (
              <button
                key={`${device.deviceId}-${index}`}
                type="button"
                onClick={() => {
                  onSelect(device.deviceId);
                  onCloseMenu();
                }}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  device.deviceId === selectedDeviceId
                    ? "bg-primary/5 text-primary font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span className="truncate mr-2">{device.label}</span>
                {device.deviceId === selectedDeviceId && <Check className="w-4 h-4 shrink-0" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
